import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ntd, newKey, dt, STAGE_LABEL } from '@/lib/format';
import {
  Banner,
  Card,
  Empty,
  Field,
  PageHeader,
  Tag,
  inputStyle,
  useToast,
} from '@/components/ui';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';

const ACTIVE = ['BOOKED', 'CHECKIN', 'CONSULT', 'TREATING'];

interface Profile {
  wallet: { balanceCents: number };
  tickets: { id: string; treatmentName: string; remaining: number; total: number }[];
  history: { id: string; startAt: string; stage: string; treatmentName: string; doctorName: string }[];
}
interface CheckoutResult {
  appointment: { stage: string };
  payment: { method: string } | null;
  invoice: { number: string; amountCents: number } | null;
  nextAppointment: { startAt: string } | null;
  warnings: string[];
}

type Pay = 'TICKET' | 'WALLET' | 'NONE';

export function CheckoutPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [sp] = useSearchParams();
  const [customerId, setCustomerId] = useState(sp.get('customerId') ?? '');
  const [pickerLabel] = useState(sp.get('name') ?? '');
  const [apptId, setApptId] = useState(sp.get('appointmentId') ?? '');
  const [pay, setPay] = useState<Pay>('NONE');
  const [ticketId, setTicketId] = useState('');
  const [amount, setAmount] = useState(0);
  const [doInvoice, setDoInvoice] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [invoiceCategory, setInvoiceCategory] = useState<'medical' | 'goods'>('medical');
  const [carrier, setCarrier] = useState('');
  const [doNext, setDoNext] = useState(false);
  const [nextDate, setNextDate] = useState('');
  const [key, setKey] = useState(newKey());
  const [result, setResult] = useState<CheckoutResult | null>(null);

  const profile = useQuery({
    queryKey: ['customer-profile', customerId],
    queryFn: () => api.get<Profile>(`/customers/${customerId}/profile`),
    enabled: !!customerId,
  });

  const activeAppts = useMemo(
    () => (profile.data?.history ?? []).filter((h) => ACTIVE.includes(h.stage)),
    [profile.data],
  );

  // 顧客變更 → 自動選第一筆待結帳預約並重置
  useEffect(() => {
    if (!apptId && activeAppts.length) setApptId(activeAppts[0].id);
  }, [activeAppts, apptId]);

  const checkout = useMutation({
    mutationFn: () =>
      api.post<CheckoutResult>('/checkout', {
        appointmentId: apptId,
        idempotencyKey: key,
        payment:
          pay === 'TICKET'
            ? { method: 'TICKET', ticketId, count: 1 }
            : pay === 'WALLET'
              ? { method: 'WALLET', amountCents: Math.round(amount * 100) }
              : { method: 'NONE' },
        invoice: doInvoice ? { amountCents: Math.round(invoiceAmount * 100), category: invoiceCategory, carrier: carrier || undefined } : undefined,
        nextBooking: doNext && nextDate ? { startAt: new Date(nextDate).toISOString() } : undefined,
      }),
    onSuccess: (r) => {
      setResult(r);
      if (r.warnings.length) fail(new Error(r.warnings.join('；')));
      else ok('結帳完成');
      void qc.invalidateQueries({ queryKey: ['customer-profile', customerId] });
      // 重置流程(換下一筆),產生新冪等鍵
      setApptId('');
      setPay('NONE');
      setTicketId('');
      setAmount(0);
      setDoInvoice(false);
      setDoNext(false);
      setKey(newKey());
    },
    onError: fail,
  });

  const appt = activeAppts.find((a) => a.id === apptId);
  const bal = profile.data?.wallet.balanceCents ?? 0;
  const tickets = profile.data?.tickets ?? [];
  const canPay =
    pay === 'NONE' ||
    (pay === 'TICKET' && ticketId) ||
    (pay === 'WALLET' && amount > 0 && Math.round(amount * 100) <= bal);

  return (
    <div>
      <PageHeader title="一鍵結帳" subtitle="完工 → 收款 → 開發票 → 約下次,一個動線走完" />
      <Banner msg={msg} />

      <div style={{ marginBottom: 16, maxWidth: 340 }}>
        <CustomerPicker value={customerId} onChange={(id) => { setCustomerId(id); setApptId(''); setResult(null); }} initialLabel={pickerLabel} />
      </div>

      {result && (
        <Card title="結帳結果" icon="✅">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
            <Tag tone="success">完工 {STAGE_LABEL[result.appointment.stage] ?? result.appointment.stage}</Tag>
            {result.payment && <Tag tone="wallet">已收款 · {result.payment.method === 'TICKET' ? '堂票' : '儲值'}</Tag>}
            {result.invoice && <Tag tone="ticket">發票 {result.invoice.number}</Tag>}
            {result.nextAppointment && <Tag tone="primary">已約下次 {dt(result.nextAppointment.startAt)}</Tag>}
            {result.warnings.map((w) => <Tag key={w} tone="warn">{w}</Tag>)}
          </div>
        </Card>
      )}

      {!customerId ? (
        <Card><Empty>請先搜尋並選擇顧客,帶出其待結帳預約。</Empty></Card>
      ) : activeAppts.length === 0 ? (
        <Card><Empty>此顧客目前無待結帳(進行中 / 已預約)的預約。</Empty></Card>
      ) : (
        <Card title="結帳" icon="🧾">
          <Field label="選擇要結帳的預約">
            <Select
              value={apptId}
              onChange={setApptId}
              options={activeAppts.map((a) => ({
                value: a.id,
                label: `${dt(a.startAt)} · ${a.treatmentName} · ${a.doctorName} · ${STAGE_LABEL[a.stage] ?? a.stage}`,
              }))}
            />
          </Field>

          {appt && (
            <>
              <div style={{ height: 1, background: 'var(--line-soft)', margin: '14px 0' }} />

              {/* 收款 */}
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>收款方式</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {(['TICKET', 'WALLET', 'NONE'] as Pay[]).map((m) => (
                  <button
                    key={m}
                    className={pay === m ? 'btn primary' : 'btn ghost'}
                    onClick={() => setPay(m)}
                  >
                    {m === 'TICKET' ? '堂票核銷' : m === 'WALLET' ? '儲值扣款' : '不收款'}
                  </button>
                ))}
              </div>
              {pay === 'TICKET' && (
                <Field label="選擇堂票">
                  {tickets.length === 0 ? (
                    <Empty>此顧客無可用堂票。</Empty>
                  ) : (
                    <Select
                      value={ticketId}
                      onChange={setTicketId}
                      placeholder="選擇堂票…"
                      options={tickets.map((t) => ({ value: t.id, label: `${t.treatmentName}（剩 ${t.remaining}/${t.total}）` }))}
                    />
                  )}
                </Field>
              )}
              {pay === 'WALLET' && (
                <Field label={`儲值扣款金額(餘額 ${ntd(bal)})`}>
                  <input type="number" style={inputStyle} value={amount || ''} onChange={(e) => setAmount(Number(e.target.value))} placeholder="0" />
                  {amount > 0 && Math.round(amount * 100) > bal && (
                    <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>餘額不足</div>
                  )}
                </Field>
              )}

              <div style={{ height: 1, background: 'var(--line-soft)', margin: '14px 0' }} />

              {/* 發票 */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: doInvoice ? 8 : 0 }}>
                <input type="checkbox" checked={doInvoice} onChange={(e) => setDoInvoice(e.target.checked)} /> 開立發票
              </label>
              {doInvoice && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <Field label="單據類別">
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className={invoiceCategory === 'medical' ? 'btn primary' : 'btn ghost'} style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => setInvoiceCategory('medical')}>醫療(免稅收據)</button>
                      <button className={invoiceCategory === 'goods' ? 'btn primary' : 'btn ghost'} style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => setInvoiceCategory('goods')}>商品(應稅發票)</button>
                    </div>
                  </Field>
                  <Field label="金額">
                    <input type="number" style={inputStyle} value={invoiceAmount || ''} onChange={(e) => setInvoiceAmount(Number(e.target.value))} placeholder="0" />
                  </Field>
                  <Field label="載具 / 統編(選填)">
                    <input style={inputStyle} value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="/ABC1234 或統編" />
                  </Field>
                </div>
              )}

              <div style={{ height: 1, background: 'var(--line-soft)', margin: '14px 0' }} />

              {/* 約下次 */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: doNext ? 8 : 0 }}>
                <input type="checkbox" checked={doNext} onChange={(e) => setDoNext(e.target.checked)} /> 預約下次回診(同療程 / 同醫師)
              </label>
              {doNext && (
                <Field label="下次時間">
                  <input type="datetime-local" style={{ ...inputStyle, width: 240 }} value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
                </Field>
              )}

              <div style={{ marginTop: 18 }}>
                <button
                  className="btn primary"
                  style={{ width: '100%', padding: 12, fontSize: 15 }}
                  disabled={!canPay || checkout.isPending}
                  onClick={() => checkout.mutate()}
                >
                  完成結帳
                </button>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
